import {
  useMemo,
  useState,
} from 'react';

import {
  useToast,
} from '@/components/base';

import {
  type CaseDeadline,
  useCaseDeadlines,
} from '@/hooks/useCaseDeadlines';

import {
  buildCaseDeadlineOverviewSummary,
  classifyOperationalDeadline,
  compareOperationalDeadlines,
  getLocalDateOnly,
  isOperationalDeadlineActive,
  matchesDeadlineWindow,
  type DeadlineWindowFilter,
} from '@/lib/caseDeadlineOverview';

const formatDate =
  (
    value:
      string |
      null |
      undefined
  ) => {
    if (!value) {
      return 'Sem data';
    }

    const parsed =
      new Date(
        `${value}T12:00:00`
      );

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return value;
    }

    return parsed.toLocaleDateString(
      'pt-BR'
    );
  };

const timingLabel =
  (
    deadline:
      CaseDeadline,

    today:
      string
  ) => {
    const timing =
      classifyOperationalDeadline(
        deadline,
        today
      );

    if (
      timing.state ===
      'overdue'
    ) {
      const amount =
        Math.abs(
          timing.daysUntil ??
          0
        );

      return amount ===
        1
        ? 'Vencido há 1 dia'
        : `Vencido há ${amount} dias`;
    }

    if (
      timing.state ===
      'today'
    ) {
      return 'Vence hoje';
    }

    if (
      timing.state ===
      'unscheduled'
    ) {
      return 'Sem data confirmada';
    }

    if (
      timing.daysUntil ===
      1
    ) {
      return 'Vence amanhã';
    }

    if (
      timing.daysUntil !==
      null
    ) {
      return `Vence em ${timing.daysUntil} dias`;
    }

    return '';
  };

const timingClassName =
  (
    deadline:
      CaseDeadline,

    today:
      string
  ) => {
    const timing =
      classifyOperationalDeadline(
        deadline,
        today
      );

    if (
      timing.state ===
      'overdue'
    ) {
      return 'border-red-200 bg-red-50 text-red-700';
    }

    if (
      timing.state ===
      'today'
    ) {
      return 'border-amber-200 bg-amber-50 text-amber-700';
    }

    if (
      timing.state ===
      'unscheduled'
    ) {
      return 'border-background-200 bg-background-100 text-foreground-600';
    }

    if (
      timing.daysUntil !==
        null &&
      timing.daysUntil <=
        3
    ) {
      return 'border-orange-200 bg-orange-50 text-orange-700';
    }

    if (
      timing.daysUntil !==
        null &&
      timing.daysUntil <=
        7
    ) {
      return 'border-blue-200 bg-blue-50 text-blue-700';
    }

    return 'border-background-200 bg-background-50 text-foreground-600';
  };

const riskLabel =
  (
    value:
      string
  ) => {
    if (
      value ===
      'high'
    ) {
      return 'Alto';
    }

    if (
      value ===
      'low'
    ) {
      return 'Baixo';
    }

    return 'Médio';
  };

const riskClassName =
  (
    value:
      string
  ) => {
    if (
      value ===
      'high'
    ) {
      return 'border-red-200 bg-red-50 text-red-700';
    }

    if (
      value ===
      'low'
    ) {
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }

    return 'border-amber-200 bg-amber-50 text-amber-700';
  };

const statusLabel =
  (
    value:
      string
  ) =>
    value ===
    'in_progress'
      ? 'Em andamento'
      : 'Pendente';

export default function CaseDeadlinesCentral() {
  const {
    addToast,
  } =
    useToast();

  const {
    caseDeadlines,
    isLoadingCaseDeadlines,
    isSavingCaseDeadline,
    caseDeadlinesError,
    loadCaseDeadlines,
    updateCaseDeadline,
  } =
    useCaseDeadlines();

  const [
    search,
    setSearch,
  ] =
    useState(
      ''
    );

  const [
    windowFilter,
    setWindowFilter,
  ] =
    useState<
      DeadlineWindowFilter
    >(
      'all'
    );

  const [
    riskFilter,
    setRiskFilter,
  ] =
    useState(
      ''
    );

  const [
    responsibleFilter,
    setResponsibleFilter,
  ] =
    useState(
      ''
    );

  const today =
    getLocalDateOnly();

  const activeDeadlines =
    useMemo(
      () =>
        caseDeadlines.filter(
          (
            deadline
          ) =>
            isOperationalDeadlineActive(
              deadline.status
            )
        ),
      [
        caseDeadlines,
      ]
    );

  const summary =
    useMemo(
      () =>
        buildCaseDeadlineOverviewSummary(
          caseDeadlines,
          today
        ),
      [
        caseDeadlines,
        today,
      ]
    );

  const responsibleOptions =
    useMemo(
      () =>
        Array.from(
          new Set(
            activeDeadlines
              .map(
                (
                  deadline
                ) =>
                  deadline.responsibleUser
                    ?.trim() ??
                  ''
              )
              .filter(
                Boolean
              )
          )
        ).sort(
          (
            left,
            right
          ) =>
            left.localeCompare(
              right,
              'pt-BR'
            )
        ),
      [
        activeDeadlines,
      ]
    );

  const filteredDeadlines =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();

        return activeDeadlines
          .filter(
            (
              deadline
            ) => {
              if (
                !matchesDeadlineWindow(
                  deadline,
                  today,
                  windowFilter
                )
              ) {
                return false;
              }

              if (
                riskFilter &&
                deadline.riskLevel !==
                  riskFilter
              ) {
                return false;
              }

              if (
                responsibleFilter &&
                (
                  deadline.responsibleUser ??
                  ''
                ) !==
                  responsibleFilter
              ) {
                return false;
              }

              if (!term) {
                return true;
              }

              return [
                deadline.title,
                deadline.caseTitle,
                deadline.clientName,
                deadline.responsibleUser,
                deadline.legalBasisSnapshot,
                deadline.ruleNameSnapshot,
              ]
                .filter(
                  Boolean
                )
                .some(
                  (
                    value
                  ) =>
                    String(
                      value
                    )
                      .toLowerCase()
                      .includes(
                        term
                      )
                );
            }
          )
          .sort(
            (
              left,
              right
            ) =>
              compareOperationalDeadlines(
                left,
                right,
                today
              )
          );
      },
      [
        activeDeadlines,
        responsibleFilter,
        riskFilter,
        search,
        today,
        windowFilter,
      ]
    );

  const cards = [
    {
      key:
        'overdue' as const,

      label:
        'Vencidos',

      value:
        summary.overdue,

      icon:
        'ri-alarm-warning-line',

      className:
        'border-red-200 bg-red-50 text-red-700',
    },

    {
      key:
        'today' as const,

      label:
        'Vencem hoje',

      value:
        summary.today,

      icon:
        'ri-time-line',

      className:
        'border-amber-200 bg-amber-50 text-amber-700',
    },

    {
      key:
        '3' as const,

      label:
        'Próximos 3 dias',

      value:
        summary.next3,

      icon:
        'ri-calendar-event-line',

      className:
        'border-orange-200 bg-orange-50 text-orange-700',
    },

    {
      key:
        '7' as const,

      label:
        'Próximos 7 dias',

      value:
        summary.next7,

      icon:
        'ri-calendar-check-line',

      className:
        'border-blue-200 bg-blue-50 text-blue-700',
    },

    {
      key:
        '15' as const,

      label:
        'Próximos 15 dias',

      value:
        summary.next15,

      icon:
        'ri-calendar-2-line',

      className:
        'border-primary-200 bg-primary-50 text-primary-700',
    },

    {
      key:
        'unscheduled' as const,

      label:
        'Sem data',

      value:
        summary.unscheduled,

      icon:
        'ri-question-line',

      className:
        'border-background-200 bg-background-100 text-foreground-600',
    },
  ];

  const handleComplete =
    async (
      deadline:
        CaseDeadline
    ) => {
      const confirmed =
        window.confirm(
          `Concluir o prazo "${deadline.title}"? A tarefa vinculada também será concluída.`
        );

      if (!confirmed) {
        return;
      }

      try {
        await updateCaseDeadline(
          deadline.id,
          {
            status:
              'completed',
          }
        );

        addToast(
          'Prazo concluído com sucesso.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível concluir o prazo.',
          'error'
        );
      }
    };

  const handleStart =
    async (
      deadline:
        CaseDeadline
    ) => {
      try {
        await updateCaseDeadline(
          deadline.id,
          {
            status:
              'in_progress',
          }
        );

        addToast(
          'Prazo marcado como em andamento.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível atualizar o prazo.',
          'error'
        );
      }
    };

  return (
    <section className="rounded-2xl border border-background-200 bg-background-50 shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-background-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <i className="ri-alarm-warning-line text-primary-600"></i>

            <h3 className="text-base font-semibold text-foreground-900">
              Central Operacional de Prazos
            </h3>
          </div>

          <p className="text-xs text-foreground-500 mt-1">
            Acompanhamento dos prazos concretos dos casos. Datas sugeridas pelo sistema não substituem a data final conferida.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            void loadCaseDeadlines()
          }
          disabled={isLoadingCaseDeadlines}
          className="inline-flex items-center gap-2 self-start rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60"
        >
          <i
            className={`ri-refresh-line ${
              isLoadingCaseDeadlines
                ? 'animate-spin'
                : ''
            }`}
          ></i>

          Atualizar central
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          {cards.map(
            (
              card
            ) => (
              <button
                key={card.key}
                type="button"
                onClick={() =>
                  setWindowFilter(
                    (
                      current
                    ) =>
                      current ===
                      card.key
                        ? 'all'
                        : card.key
                  )
                }
                className={`rounded-xl border p-3 text-left transition-all ${
                  windowFilter ===
                  card.key
                    ? `${card.className} ring-1 ring-current`
                    : 'border-background-200 bg-background-50 hover:bg-background-100 text-foreground-700'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <i className={`${card.icon} text-lg`}></i>

                  <span className="text-xl font-bold">
                    {card.value}
                  </span>
                </div>

                <p className="text-[11px] font-medium mt-2">
                  {card.label}
                </p>
              </button>
            )
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">
              Prazos ativos
            </p>

            <p className="text-lg font-bold text-foreground-900 mt-1">
              {summary.active}
            </p>
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-[10px] uppercase tracking-wider text-red-500">
              Alto risco
            </p>

            <p className="text-lg font-bold text-red-700 mt-1">
              {summary.highRisk}
            </p>
          </div>

          <div className="rounded-lg border border-background-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">
              Exibidos
            </p>

            <p className="text-lg font-bold text-foreground-900 mt-1">
              {filteredDeadlines.length}
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setWindowFilter(
                'all'
              );

              setRiskFilter(
                ''
              );

              setResponsibleFilter(
                ''
              );

              setSearch(
                ''
              );
            }}
            className="rounded-lg border border-background-200 p-3 text-left hover:bg-background-100"
          >
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">
              Filtros
            </p>

            <p className="text-sm font-semibold text-primary-700 mt-1">
              Limpar todos
            </p>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="md:col-span-2 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              className="w-full pl-9 pr-3 py-2 border border-background-200 rounded-lg text-sm focus:outline-none focus:border-primary-300"
              placeholder="Buscar por prazo, cliente, caso, responsável..."
            />
          </div>

          <select
            value={riskFilter}
            onChange={(event) =>
              setRiskFilter(
                event.target.value
              )
            }
            className="px-3 py-2 border border-background-200 rounded-lg bg-background-50 text-sm"
          >
            <option value="">
              Todos os riscos
            </option>

            <option value="high">
              Alto risco
            </option>

            <option value="medium">
              Médio risco
            </option>

            <option value="low">
              Baixo risco
            </option>
          </select>

          <select
            value={responsibleFilter}
            onChange={(event) =>
              setResponsibleFilter(
                event.target.value
              )
            }
            className="px-3 py-2 border border-background-200 rounded-lg bg-background-50 text-sm"
          >
            <option value="">
              Todos os responsáveis
            </option>

            {responsibleOptions.map(
              (
                responsible
              ) => (
                <option
                  key={responsible}
                  value={responsible}
                >
                  {responsible}
                </option>
              )
            )}
          </select>
        </div>

        {caseDeadlinesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {caseDeadlinesError}
          </div>
        )}

        {isLoadingCaseDeadlines && (
          <div className="rounded-lg border border-dashed border-background-200 p-6 text-center text-sm text-foreground-500">
            Carregando prazos operacionais...
          </div>
        )}

        {!isLoadingCaseDeadlines &&
          !caseDeadlinesError &&
          filteredDeadlines.length ===
            0 && (
            <div className="rounded-lg border border-dashed border-background-200 p-6 text-center">
              <i className="ri-calendar-check-line text-2xl text-foreground-300"></i>

              <p className="text-sm font-medium text-foreground-700 mt-2">
                Nenhum prazo encontrado
              </p>

              <p className="text-xs text-foreground-500 mt-1">
                Não há prazo ativo correspondente aos filtros selecionados.
              </p>
            </div>
          )}

        {!isLoadingCaseDeadlines &&
          filteredDeadlines.length >
            0 && (
            <div className="overflow-x-auto border border-background-200 rounded-xl">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="bg-background-100/60 border-b border-background-200">
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Prazo
                    </th>

                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Caso / Cliente
                    </th>

                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Data
                    </th>

                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Risco
                    </th>

                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Responsável
                    </th>

                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-foreground-500">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredDeadlines.map(
                    (
                      deadline
                    ) => (
                      <tr
                        key={deadline.id}
                        className="border-b border-background-100 last:border-0 hover:bg-background-50"
                      >
                        <td className="px-3 py-3 align-top">
                          <p className="text-sm font-semibold text-foreground-800">
                            {deadline.title}
                          </p>

                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[10px] text-foreground-500">
                              {statusLabel(
                                deadline.status
                              )}
                            </span>

                            {deadline.taskId && (
                              <span className="text-[10px] text-emerald-700">
                                • tarefa sincronizada
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <p className="text-xs font-medium text-foreground-700">
                            {deadline.caseTitle ||
                              'Caso sem título'}
                          </p>

                          <p className="text-[11px] text-foreground-500 mt-0.5">
                            {deadline.clientName ||
                              'Cliente não informado'}
                          </p>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <p className="text-sm font-semibold text-foreground-800">
                            {formatDate(
                              deadline.dueDate
                            )}
                          </p>

                          <span
                            className={`inline-flex mt-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${timingClassName(
                              deadline,
                              today
                            )}`}
                          >
                            {timingLabel(
                              deadline,
                              today
                            )}
                          </span>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${riskClassName(
                              deadline.riskLevel
                            )}`}
                          >
                            {riskLabel(
                              deadline.riskLevel
                            )}
                          </span>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <p className="text-xs text-foreground-700">
                            {deadline.responsibleUser ||
                              'Sem responsável'}
                          </p>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <div className="flex items-center justify-end gap-1.5">
                            {deadline.status ===
                              'pending' && (
                              <button
                                type="button"
                                onClick={() =>
                                  void handleStart(
                                    deadline
                                  )
                                }
                                disabled={isSavingCaseDeadline}
                                className="px-2 py-1 rounded-md border border-blue-200 text-[11px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              >
                                Iniciar
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() =>
                                void handleComplete(
                                  deadline
                                )
                              }
                              disabled={isSavingCaseDeadline}
                              className="px-2 py-1 rounded-md border border-emerald-200 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              Concluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

        <p className="text-[11px] text-foreground-400">
          Janelas de 3, 7 e 15 dias consideram somente prazos futuros e são cumulativas. Prazos vencidos e vencendo hoje aparecem em categorias próprias.
        </p>
      </div>
    </section>
  );
}